import { ClientLogger } from 'bernie-client';

import { BadgeAdapter } from 'stores/adapters/bex-api/search/badge-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

import {
  AllHotelProducts,
  DataAttributes,
  DisclaimerViewModel,
  GalleryViewModel,
  HotelFlags,
  HotelViewModel,
  ImageViewModel,
  ListingFooterViewModel,
  MessagingCardViewModel,
  PinnedHotelViewModel,
  PriceViewModel,
  PrimaryCardMessagingModel,
  RecentlyViewedModel,
  ReviewsViewModel,
  ShoppingGlobal,
  ShortlistModel,
  TravelAd
} from 'models';
import { ResultType } from 'constants/resultType';
import { ThemeName } from 'constants/theme';

/* tslint:disable:max-file-line-count */

import {
  CommonPropertyCardFields,
  FullPropertySearch,
  PropertyMapMigration,
  PropertySearchTravelAds,
  StrikeOutType
} from '__generated__/api';
import { MessagingCardProps } from 'components/common/messaging-card';

import MessageContent = FullPropertySearch.MessageContent;
import _MessageContent = FullPropertySearch._MessageContent;
import ClickTracking = FullPropertySearch.ClickTracking;

const propertyType = 'HotelViewModel';
const logger = ClientLogger.getLoggerWithIdentifier('pwa.adapter.bex-api.search-results');

export const mapPropertySearchResults = (
  query: FullPropertySearch.Query | PropertyMapMigration.Query | PropertySearchTravelAds.Query
): HotelViewModel[] => {
  const properties = query.propertySearch.properties;

  if (!properties) {
    return null;
  }

  return (properties as Array<PropertyMapMigration.Properties | FullPropertySearch.Properties | PropertySearchTravelAds.Properties>).map(
    mapProperty
  );
};

export const mapSearchedDestination = (query: FullPropertySearch.Query): string | null =>
  query.propertySearch.summary.region && query.propertySearch.summary.region.name;

const mapProperty = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): HotelViewModel => {
  const id = getPropertyId(property);
  const priceModel = getPriceModel(property);
  const propertyReviews = getReviewsModel(property.reviews);
  const primaryCardMessages = getPrimaryCardMessages(property);
  const propertyFlags = getHotelFlags(property);
  const image = getThumbnail(property);
  const badge = BadgeAdapter.getPropertyListingBadgeModel(property);
  const pinned = getPinnedHotelViewModel(property);
  const gallery = getGalleryModel(property.propertyGallery);

  return {
    hotelName: property.name,
    name: property.name,
    hotelId: id,
    id,
    infositeUrl: property.infositeURL,
    stars: getStarRating(property),
    starRatingIcon: property.starRatingIcon,
    latitude: getCoordinate(property, 'latitude'),
    longitude: getCoordinate(property, 'longitude'),
    type: propertyType,
    resultType: ResultType.HOTEL,
    displayPrice: priceModel.lead,
    neighborhood: getLocationMessaging(property),
    hasAvailableOffer: propertyFlags.hasAvailableOffer,
    freeCancel: propertyFlags.freeCancel,
    vip: propertyFlags.isVip,
    propertyListingBadgeModel: badge,
    price: priceModel,
    reviews: propertyReviews,
    primaryCardMessages,
    disclaimerDialog: getDisclaimerViewModel(property),
    hotelFlags: propertyFlags,
    thumbnail: image,
    pinnedDetails: pinned,
    vipMessage: property.vipMessaging,
    supportingMessages: property.supportingMessages,
    shortlistModel: getShortList(property),
    travelAd: getTravelAd(property),
    newHotel: propertyFlags.newHotel,
    gallery,
    propertySummary: property.highlightedPropertyDetail,
    recentlyViewed: getRecentlyViewed(property),
    dataAttributes: adaptDataAttributes(property),
    listingFooter: mapListingFooter(property)
  };
};

export const getGalleryModel = (
  gallery: FullPropertySearch.PropertyGallery[] | PropertyMapMigration.PropertyGallery[]
): GalleryViewModel => {
  if (!gallery) {
    return;
  }

  return {
    images: gallery.map(entry => ({
      caption: entry.image.description,
      alt: entry.image.description,
      src: entry.image.url,
      ...(entry.subjectId && { subjectId: entry.subjectId }),
      fallBackImage: entry.fallbackImage && entry.fallbackImage.url
    }))
  };
};

// TODO: Change PWA model to string
const getPropertyId = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): number | null =>
  property.id ? Number.parseInt(property.id) : null;

/**
 * Only return the star rating on a valid number
 */
export const getStarRating = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): number | null =>
  property.star && property.star > 0 ? property.star : null;

/**
 * Return distance from messaging if available, otherwise returns neighborhood
 */
export const getLocationMessaging = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): string | null => {
  if (property.destinationInfo && property.destinationInfo.distanceFromMessaging) {
    return property.destinationInfo.distanceFromMessaging;
  }

  return property.neighborhood ? property.neighborhood.name : null;
};

/**
 * Return coordinates, or null if there are no matching fields
 */
const getCoordinate = (
  property: FullPropertySearch.Properties | PropertyMapMigration.Properties,
  keyName: 'latitude' | 'longitude'
): string | null => {
  const value = property.mapMarker && property.mapMarker.latLong && property.mapMarker.latLong[keyName];

  return value ? value.toString() : null;
};

/**
 * Always return a price model but the fields can be null
 */
export const getPriceModel = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): PriceViewModel => {
  const price: CommonPropertyCardFields.Price | null = property.price;

  return {
    lead: price && price.lead && price.lead.formatted,
    strikeOut: price && price.strikeOut && price.strikeOut.formatted,
    strikeOutType: (price && price.strikeOutType) || StrikeOutType.INVALID,
    strikeoutDisclaimer: price && price.disclaimer && price.disclaimer.value,
    priceMessage:
      property.availability && property.availability.available
        ? null
        : {
            text: property.availability && property.availability.text,
            theme: ThemeName[property.availability.theme]
          },
    numberOfNightsAndRooms: price && price.roomNightMessage,
    priceMessages: price && price.priceMessages,
    marketingFeeDetails: price && price.marketingFeeDetails,
    options: price && price.options
  };
};

/**
 * Property reviews are non-null
 */
export const getReviewsModel = (reviews: CommonPropertyCardFields.Reviews): ReviewsViewModel => ({
  overallRating: reviews.score,
  localizedOverallRating: reviews.localizedScore,
  count: reviews.total,
  localizedCount: reviews.localizedTotal,
  superlative: reviews.superlative
});

/**
 * Property Primary messages model, used when sending multiple messages
 */
export const getPrimaryCardMessages = (
  property: FullPropertySearch.Properties | PropertyMapMigration.Properties
): PrimaryCardMessagingModel[] => {
  const offerSummary: FullPropertySearch.Messages[] | null = property.offerSummary.messages;
  if (offerSummary && offerSummary.length > 0) {
    return offerSummary.map(summary => ({
      description: summary.message,
      theme: ThemeName[summary.theme],
      type: summary.type
    }));
  }

  return null;
};

/**
 * Property Legal Disclaimer messages model
 */
export const getDisclaimerViewModel = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): DisclaimerViewModel => {
  const legalDisclaimer: FullPropertySearch.LegalDisclaimer | null = property.legalDisclaimer;
  if (!legalDisclaimer) {
    return null;
  }

  return {
    disclaimerLink: legalDisclaimer.title,
    disclaimerContent: legalDisclaimer.disclaimerContents
  };
};

/**
 * Property availability is non-null
 */
export const getHotelFlags = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): HotelFlags => ({
  hasAvailableOffer: property.availability.available,
  isVip: property.vipMessaging != null,
  newHotel: false
});

/**
 * map and return recentlyViewedModel if it is non-null
 */
export const getRecentlyViewed = (
  property: FullPropertySearch.Properties | PropertyMapMigration.Properties
): RecentlyViewedModel | null => {
  if (!property.recentlyViewed) {
    return null;
  }

  return {
    icon: property.recentlyViewed.id,
    message: property.recentlyViewed.description
  };
};

/**
 * Return null if there is no image, instead of a null url
 */
const getThumbnail = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): ImageViewModel | null => {
  const image: CommonPropertyCardFields.Image | null = property.image;
  if (!image) {
    return null;
  }

  return {
    caption: image.description,
    thumbnailSrc: image.url,
    src: image.url,
    fallBackImage: image.url
  };
};

/**
 * Always return a model, but the fields can be null if there is no offerBadge
 */
export const getPinnedHotelViewModel = (
  property: FullPropertySearch.Properties | PropertyMapMigration.Properties
): PinnedHotelViewModel | null => {
  if (!property.pinnedDetails) {
    return null;
  }

  return {
    heading: property.pinnedDetails.heading
  };
};

export const mapListingFooter = (
  property: FullPropertySearch.Properties | PropertyMapMigration.Properties
): ListingFooterViewModel | null => {
  const listingFooter = property.listingFooter;

  if (!listingFooter) {
    return null;
  }

  return {
    icon: listingFooter.icon ? listingFooter.icon.id : null,
    actionModel: {
      text: listingFooter.text,
      rfrr: listingFooter.referrerId,
      href: listingFooter.linkUrl,
      actionDetails: listingFooter.actionDetails
        ? {
            name: listingFooter.actionDetails.name
          }
        : null
    }
  };
};

export const adaptDataAttributes = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): DataAttributes | null => {
  if (!property.dataAttributes) {
    return null;
  }
  const listing = {};
  listing[property.dataAttributes.key] = property.dataAttributes.value;

  return {
    listing
  };
};

export const getShortList = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): ShortlistModel | null => {
  if (!property.shortList || !property.shortList.metaData) {
    return null;
  }

  const clickTracking =
    property.shortList &&
    property.shortList.clickTracking &&
    property.shortList.clickTracking.map(clickTrackData => {
      if (clickTrackData.schemaName && clickTrackData.messageContent) {
        return {
          schemaName: clickTrackData.schemaName,
          messageContent: getMessageContent(clickTrackData)
        };
      }
    });

  return {
    favorited: property.shortList.favorited,
    saveUrl: property.shortList.saveUrl,
    removeUrl: property.shortList.removeUrl,
    meta: {
      hotelId: property.shortList.metaData.hotelId,
      chkIn: property.shortList.metaData.chkIn,
      chkOut: property.shortList.metaData.chkOut,
      roomConfiguration: property.shortList.metaData.roomConfiguration
    },
    clickTracking
  };
};

export const getMessageContent = (clickTrackData: ClickTracking): ShoppingGlobal | AllHotelProducts => {
  let messageContent: ShoppingGlobal | AllHotelProducts = null;

  if (clickTrackData.messageContent.__typename === 'ShoppingGlobal') {
    const shoppingGlobal: MessageContent = clickTrackData.messageContent;

    messageContent = {
      tripStartDate: shoppingGlobal.tripStartDate,
      tripEndDate: shoppingGlobal.tripEndDate,
      destinationCode: shoppingGlobal.destinationCode,
      destinationName: shoppingGlobal.destinationName,
      numChildren: shoppingGlobal.numChildren,
      numAdults: shoppingGlobal.numAdults,
      shoppingHotel: shoppingGlobal.shoppingHotel
    };
  } else if (clickTrackData.messageContent.__typename === 'AllHotelProducts') {
    const allHotelProducts: _MessageContent = clickTrackData.messageContent;

    messageContent = {
      hotelProducts: allHotelProducts.hotelProducts
    };
  }

  return messageContent;
};

export const mapMessageModel = (message: FullPropertySearch._Messages): MessagingCardViewModel | null => {
  const title = message.title;
  const subtitle = message.subtitle;
  const messageAction = message.action;
  const primaryAction = messageAction && messageAction.primary;
  const secondaryAction = messageAction && messageAction.secondary;

  return {
    titleModel: {
      text: title && title.text,
      icon: title && title.icon && title.icon.id,
      theme: title && title.theme && title.theme.toString()
    },
    subtitleModel: {
      text: subtitle && subtitle.text,
      icon: subtitle && subtitle.icon && subtitle.icon.id,
      theme: subtitle && subtitle.theme && subtitle.theme.toString()
    },
    actionModel: {
      primary: {
        href: primaryAction && primaryAction.linkUrl,
        text: primaryAction && primaryAction.text,
        rfrr: primaryAction && primaryAction.referrerId,
        actionDetails: {
          name: primaryAction && primaryAction.actionDetails && primaryAction.actionDetails.name,
          details: primaryAction && primaryAction.actionDetails && primaryAction.actionDetails.details
        }
      },
      secondary: {
        href: secondaryAction && secondaryAction.linkUrl,
        text: secondaryAction && secondaryAction.text,
        rfrr: secondaryAction && secondaryAction.referrerId,
        actionDetails: {
          name: secondaryAction && secondaryAction.actionDetails && secondaryAction.actionDetails.name,
          details: secondaryAction && secondaryAction.actionDetails && secondaryAction.actionDetails.details
        }
      }
    }
  };
};

export const mapMessages = (summary: FullPropertySearch.Summary | PropertyMapMigration.Summary): MessagingCardProps[] => {
  const renderMessages = summary && summary.messages && summary.messages.length > 0;
  const messages =
    renderMessages &&
    (summary.messages as Array<FullPropertySearch._Messages | PropertyMapMigration._Messages>).map(message => ({
      index: message.index,
      model: mapMessageModel(message)
    }));

  return messages || [];
};

export const mapOmnitureData = (analytics: any): any | null => {
  if (Array.isArray(analytics) && analytics.length > 0 && analytics[0]) {
    return analytics[0];
  } else {
    logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_OMNITURE_PARSE_ERROR, 'Failed to parse omniture data');
  }

  return null;
};

export const getTravelAd = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): TravelAd | null => {
  const sponsoredListing = property.sponsoredListing;
  if (sponsoredListing) {
    return {
      adId: sponsoredListing.id,
      clickRedirectTrackingUrl: sponsoredListing.clickRedirectTrackingUrl,
      clickTrackingUrl: sponsoredListing.clickTrackingUrl,
      details: sponsoredListing.details,
      detailsHeadline: sponsoredListing.detailsHeadline,
      hotelImage: sponsoredListing.hotelImage,
      impressionTrackingUrl: sponsoredListing.impressionTrackingUrl,
      carouselImpressionTrackingUrl: null,
      trackingData: sponsoredListing.trackingData,
      brand: sponsoredListing.brand,
      clickRedirectLinkoffTrackingUrl: sponsoredListing.clickRedirectLinkoffTrackingUrl,
      logo: sponsoredListing.logo,
      lotagline: sponsoredListing.lotagline,
      rating: sponsoredListing.rating,
      slots: sponsoredListing.slots,
      vanityurl: sponsoredListing.vanityUrl,
      rank: sponsoredListing.rank,
      position: sponsoredListing.position,
      candidateHmGuid: '',
      beaconIssued: ''
    };
  }

  return null;
};
